import React, { useState, useEffect, useCallback } from 'react';
import { Paper,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  CircularProgress,
  Divider,
  Stack,
  Box,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  FileDownload as FileDownloadIcon,
  Visibility as VisibilityIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import approvalRequestApiService from '../../../services/approvalRequestApiService';
import accountsApiService from '../../../services/accountsApiService';
import {
  encryptText,
} from "../../../utils/encryption";

const ApprovalRequest = ({ showSnackbar, setLoading, loading }) => {
  const navigate = useNavigate();
  
  // Utility function to format dates from API response
  const formatApiDate = (dateStr) => {
    if (!dateStr) return '';
    
    try {
      // Handle date format "02 Feb 2026 12:38:10:557"
      const cleanDateStr = dateStr.split(' ').slice(0, 3).join(' ');
      const date = new Date(cleanDateStr);
      
      if (isNaN(date.getTime())) {
        return dateStr; // Return original string if parsing fails
      }
      
      return date.toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return dateStr; // Return original string if any error occurs
    }
  };
  
  // Helper function to format comma-separated values into numbered list
  const formatCommaSeparatedToNumberedList = (text) => {
    if (!text || typeof text !== 'string') return null;
    
    const items = text.split(',').map(item => item.trim()).filter(item => item);
    if (items.length === 0) return null;
    if (items.length === 1) return text; // Return as is for single item
    
    return items;
  };
  
  // State management
  const [filters, setFilters] = useState({
    transactionStatus: '2', // Default to UnProcessed
    labType: '',
    userName: '',
    transactionType: '',
    orderId: '',
  });

  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  // Dropdown options
  const [labTypes, setLabTypes] = useState([]);
  const [transactionTypes, setTransactionTypes] = useState([]);

  // Load dropdown options
  useEffect(() => {
    loadDropdownData();
  }, []);

  const loadDropdownData = async () => {
    try {
      setLoading(true);
      
      // Load lab types and transaction types
      const [paymentTypesResponse] = await Promise.all([
        approvalRequestApiService.getPaymentTypes(),
      ]);

      if (paymentTypesResponse.success) {
        setTransactionTypes(paymentTypesResponse.data || []);
      }

      // Set lab types from service
      setLabTypes(approvalRequestApiService.getLabTypes());

      // Load initial data with reset page
      await loadApprovalData({ page: 0, resetPage: true });
    } catch (error) {
      console.error('Error loading dropdown data:', error);
      showSnackbar(error.message || 'Error loading dropdown data', 'error');
    } finally {
      setLoading(false);
    }
  };
  


  const loadApprovalData = async (filterParams = {}) => {
    try {
      setLoading(true);
      
      // Get the current page from filterParams or use current state
      const currentPage = filterParams.page !== undefined ? filterParams.page : page;
      const currentPageSize = filterParams.pageSize !== undefined ? filterParams.pageSize : rowsPerPage;
      
      // Prepare API parameters
      const apiParams = {
        transactionStatus: filterParams.transactionStatus !== undefined ? filterParams.transactionStatus : filters.transactionStatus,
        labType: filterParams.labType !== undefined ? filterParams.labType : filters.labType,
        userName: filterParams.userName !== undefined ? filterParams.userName : filters.userName,
        transactionType: filterParams.transactionType !== undefined ? filterParams.transactionType : filters.transactionType,
        orderId: filterParams.orderId !== undefined ? filterParams.orderId : filters.orderId,
        page: currentPage + 1, // API expects 1-based page numbers
        pageSize: currentPageSize
      };

      const response = await approvalRequestApiService.getApprovalRequests(apiParams);

      if (response.success) {
        console.log('Approval data received:', response.data);
        setData(response.data || []);
        setFilteredData(response.data || []);
        setTotalRecords(response.pagination?.totalRecords || 0);
        // Only reset page to 0 if this is a filter change (not pagination)
        if (filterParams.resetPage === true) {
          setPage(0);
        }
      } else {
        showSnackbar(response.message || 'Failed to load approval data', 'error');
        setData([]);
        setFilteredData([]);
        setTotalRecords(0);
      }
    } catch (error) {
      console.error('Error loading approval data:', error);
      showSnackbar(error.message || 'Error loading approval data', 'error');
      setData([]);
      setFilteredData([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    const updatedFilters = {
      ...filters,
      [field]: value
    };
    
    setFilters(updatedFilters);
    
    // Automatically trigger API call when transaction status changes
    if (field === 'transactionStatus') {
      setPage(0); // Reset to first page when filters change
      loadApprovalData({ ...updatedFilters, page: 0, resetPage: true });
    }
  };

  const handleSearch = () => {
    setPage(0); // Reset to first page when searching
    loadApprovalData({ ...filters, page: 0, resetPage: true });
  };

  const handleReset = () => {
    const resetFilters = {
      transactionStatus: '2',
      labType: '',
      userName: '',
      transactionType: '',
      orderId: '',
    };
    
    setFilters(resetFilters);
    setPage(0); // Reset to first page when resetting filters
    loadApprovalData({ ...resetFilters, page: 0, resetPage: true });
  };

  const handleExcelExport = async () => {
    try {
      setLoading(true);
      
      // Use exactly the same parameters as the search/loadApprovalData function
      const exportParams = {
        transactionStatus: filters.transactionStatus,
        labType: filters.labType,
        userName: filters.userName,
        transactionType: filters.transactionType,
        orderId: filters.orderId,
        // No pagination for export - get all data
        page: 1,
        pageSize: 999999 // Large number to get all records
      };

      const response = await approvalRequestApiService.exportApprovalRequests(exportParams);
      
      if (response.success) {
        showSnackbar('Excel export completed successfully', 'success');
      } else {
        showSnackbar(response.message || 'Export failed', 'error');
      }
    } catch (error) {
      console.error('Error exporting data:', error);
      showSnackbar(error.message || 'Error exporting data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    // Reload data with new page - don't reset page
    loadApprovalData({ page: newPage });
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    // Reload data with new page size and reset to first page
    loadApprovalData({ page: 0, pageSize: newRowsPerPage });
  };

  const handleViewApplication = (row) => {
    // Determine assessment type from the row data
    let assessmentType = 'Main'; // Default to Main
    
    if (row.Type_Of_Processing && row.Type_Of_Processing.length > 0) {
      // Use the first assessment type from the array
      assessmentType = row.Type_Of_Processing[0];
    } else if (row.assessmntype) {
      // Extract type from assessmntype field
      if (row.assessmntype.toLowerCase().includes('pre')) {
        assessmentType = 'Pre';
      } else if (row.assessmntype.toLowerCase().includes('qms')) {
        assessmentType = 'QMS';
      }
    }
    
    // Navigate to approval details page with encrypted parameters
    navigate(`/main/accounts/approval-details?id=${encryptText(row.appid.toString())}&type=${encryptText(assessmentType)}`);
  };

  const getStatusChip = (row) => {
    let chipColor = 'default';
    let chipVariant = 'outlined';
    let statusText = 'Unknown';
    
    // Determine status based on Is_Processed and other fields
    if (row.Is_Processed === 1) {
      chipColor = 'success';
      chipVariant = 'filled';
      statusText = 'Processed';
    } else if (row.Is_Processed === 0) {
      chipColor = 'warning';
      chipVariant = 'filled';
      statusText = 'UnProcessed';
    }
    
    // Check if sent to CEO (this would need to be added to API response if available)
    // if (row.Is_Sent_To_CEO === 1) {
    //   chipColor = 'info';
    //   chipVariant = 'filled';
    //   statusText = 'Sent to CEO';
    // }
    
    return (
      <Chip
        label={statusText}
        size="small"
        color={chipColor}
        variant={chipVariant}
      />
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Typography variant="h4" sx={{ mb: 2 }}>
        Approval Request
      </Typography>
      <Divider sx={{ mb: 3 }} />

      {/* Filter Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <FilterListIcon sx={{ mr: 1 }} />
            <Typography variant="h6">Filter</Typography>
            <Box sx={{ ml: 'auto' }}>
              <Button
                variant="outlined"
                onClick={handleReset}
                sx={{ mr: 1 }}
                startIcon={<RefreshIcon />}
              >
                Reset Filter
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={handleExcelExport}
                startIcon={<FileDownloadIcon />}
                disabled={loading || filteredData.length === 0}
              >
                Export to Excel
              </Button>
            </Box>
          </Box>

          {/* Transaction Status Radio Buttons */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Transaction Status:</Typography>
            <RadioGroup
              row
              value={filters.transactionStatus}
              onChange={(e) => handleFilterChange('transactionStatus', e.target.value)}
            >
              <FormControlLabel value="1" control={<Radio />} label="Processed" />
              <FormControlLabel value="2" control={<Radio />} label="UnProcessed" />
              {/* <FormControlLabel value="3" control={<Radio />} label="Sent to CEO" /> */}
            </RadioGroup>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Lab Type</InputLabel>
                <Select
                  value={filters.labType}
                  label="Lab Type"
                  onChange={(e) => handleFilterChange('labType', e.target.value)}
                >
                  {labTypes.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="User Name/Lab Id"
                value={filters.userName}
                onChange={(e) => handleFilterChange('userName', e.target.value)}
                placeholder="Enter Username/Lab Id"
              />
            </Grid>
{/* 
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Transaction Type</InputLabel>
                <Select
                  value={filters.transactionType}
                  label="Transaction Type"
                  onChange={(e) => handleFilterChange('transactionType', e.target.value)}
                >
                  {transactionTypes.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Order Id"
                value={filters.orderId}
                onChange={(e) => handleFilterChange('orderId', e.target.value)}
                placeholder="Enter Order Id"
              />
            </Grid> */}
          </Grid>

          <Box sx={{ mt: 2, textAlign: 'right' }}>
            <Button
              variant="contained"
              onClick={handleSearch}
              startIcon={<SearchIcon />}
            >
              Search
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Data Table */}
      <Card sx={{ mb: 3, boxShadow: 3 }}>
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : filteredData.length === 0 ? (
            <Box sx={{ p: 3 }}>
              <Alert severity="info">
                No approval requests found. Try adjusting your filters.
              </Alert>
            </Box>
          ) : (
            <>
              <Box sx={{ 
                bgcolor: 'primary.main', 
                color: 'primary.contrastText', 
                p: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Approval Requests ({totalRecords} records)
                </Typography>
                <Chip 
                  label={`Page ${page + 1} of ${Math.ceil(totalRecords / rowsPerPage)}`}
                  size="small"
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'inherit' }}
                />
              </Box>
              
              <TableContainer sx={{ maxHeight:'80vh', overflowX: 'auto' }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ 
                        bgcolor: 'grey.100', 
                        fontWeight: 'bold',
                        borderBottom: '2px solid',
                        borderColor: 'primary.main',
                        minWidth: 180
                      }}>
                        S. No / Actions / Requested Date / Dealing Officer
                      </TableCell>
                      <TableCell sx={{ 
                        bgcolor: 'grey.100', 
                        fontWeight: 'bold',
                        borderBottom: '2px solid',
                        borderColor: 'primary.main',
                        minWidth: 300
                      }}>
                        Lab Details
                      </TableCell>
                      <TableCell sx={{ 
                        bgcolor: 'grey.100', 
                        fontWeight: 'bold',
                        borderBottom: '2px solid',
                        borderColor: 'primary.main',
                        minWidth: 250
                      }}>
                        Contact Details
                      </TableCell>
                      <TableCell sx={{ 
                        bgcolor: 'grey.100', 
                        fontWeight: 'bold',
                        borderBottom: '2px solid',
                        borderColor: 'primary.main',
                        minWidth: 180
                      }}>
                        Discipline / Group
                      </TableCell>
                      <TableCell sx={{ 
                        bgcolor: 'grey.100', 
                        fontWeight: 'bold',
                        borderBottom: '2px solid',
                        borderColor: 'primary.main',
                        minWidth: 200
                      }}>
                        Assessment Date / Assessor
                      </TableCell>
                      <TableCell sx={{ 
                        bgcolor: 'grey.100', 
                        fontWeight: 'bold',
                        borderBottom: '2px solid',
                        borderColor: 'primary.main',
                        minWidth: 130
                      }}>
                        Processed Date
                      </TableCell>
                      <TableCell sx={{ 
                        bgcolor: 'grey.100', 
                        fontWeight: 'bold',
                        borderBottom: '2px solid',
                        borderColor: 'primary.main',
                        minWidth: 150
                      }}>
                        Processed By
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredData.map((row, index) => (
                      <TableRow 
                        key={row.appid || index} 
                        hover
                        sx={{
                          '&:nth-of-type(odd)': {
                            backgroundColor: 'rgba(0, 0, 0, 0.02)',
                          },
                          '&:hover': {
                            backgroundColor: 'rgba(25, 118, 210, 0.08)',
                            transform: 'scale(1.001)',
                            transition: 'all 0.2s ease-in-out',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                          },
                          cursor: 'pointer'
                        }}
                      >
                        <TableCell sx={{ 
                          borderBottom: '1px solid rgba(224, 224, 224, 0.5)',
                          py: 2
                        }}>
                          <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            gap: 1.5,
                            p: 1,
                            bgcolor: 'rgba(0,0,0,0.02)',
                            borderRadius: 2,
                            border: '1px solid rgba(224, 224, 224, 0.3)'
                          }}>
                            {/* Serial Number and Action */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Chip
                                label={page * rowsPerPage + index + 1}
                                size="small"
                                variant="outlined"
                                sx={{ 
                                  minWidth: '35px',
                                  height: '24px',
                                  fontSize: '0.75rem',
                                  fontWeight: 'bold'
                                }}
                              />
                              <Tooltip title="View Application Details">
                                <IconButton
                                  size="small"
                                  onClick={() => handleViewApplication(row)}
                                  sx={{
                                    bgcolor: 'primary.main',
                                    color: 'white',
                                    '&:hover': {
                                      bgcolor: 'primary.dark',
                                      transform: 'scale(1.1)'
                                    },
                                    transition: 'all 0.2s ease'
                                  }}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                            
                            {/* Requested Date */}
                            <Box sx={{ 
                              display: 'flex', 
                              flexDirection: 'column',
                              gap: 0.5
                            }}>
                              <Typography variant="caption" sx={{ 
                                fontWeight: 'bold', 
                                color: 'text.primary',
                                textTransform: 'uppercase',
                                fontSize: '0.7rem',
                                letterSpacing: '0.5px'
                              }}>
                                Requested Date:
                              </Typography>
                              <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center',
                                gap: 1,
                                pl: 0.5
                              }}>
                                <Box sx={{
                                  width: 6,
                                  height: 6,
                                  borderRadius: '50%',
                                  bgcolor: 'info.main'
                                }} />
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {formatApiDate(row.RequestDate)}
                                </Typography>
                              </Box>
                            </Box>
                            
                            {/* Dealing Officer */}
                            <Box sx={{ 
                              display: 'flex', 
                              flexDirection: 'column',
                              gap: 0.5
                            }}>
                              <Typography variant="caption" sx={{ 
                                fontWeight: 'bold', 
                                color: 'text.primary',
                                textTransform: 'uppercase',
                                fontSize: '0.7rem',
                                letterSpacing: '0.5px'
                              }}>
                                Dealing Officer:
                              </Typography>
                              <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center',
                                gap: 1,
                                pl: 0.5
                              }}>
                                <Box sx={{
                                  width: 6,
                                  height: 6,
                                  borderRadius: '50%',
                                  bgcolor: 'success.main'
                                }} />
                                <Typography variant="body2" sx={{ 
                                  fontWeight: 500,
                                  color: 'text.primary'
                                }}>
                                  {row.DOname || 'Not Assigned'}
                                </Typography>
                              </Box>
                            </Box>
                            {/* <Box sx={{ 
                              display: 'flex', 
                              flexDirection: 'column',
                              gap: 0.5
                            }}>
                              <Typography variant="caption" sx={{ 
                                fontWeight: 'bold', 
                                color: 'text.primary',
                                textTransform: 'uppercase',
                                fontSize: '0.7rem',
                                letterSpacing: '0.5px'
                              }}>
                                Request Date:
                              </Typography>
                              <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center',
                                gap: 1,
                                pl: 0.5
                              }}>
                                <Box sx={{
                                  width: 6,
                                  height: 6,
                                  borderRadius: '50%',
                                  bgcolor: 'success.main'
                                }} />
                                <Typography variant="body2" sx={{ 
                                  fontWeight: 500,
                                  color: 'text.primary'
                                }}>
                                  {row.RequestDate ? formatApiDate(row.RequestDate) : 'N/A'}
                                </Typography>
                              </Box>
                            </Box> */}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ 
                            borderBottom: '1px solid rgba(224, 224, 224, 0.5)',
                          py: 2
                        }}>
                          <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            gap: 1,
                            p: 1,
                            bgcolor: 'rgba(0,0,0,0.02)',
                            borderRadius: 2,
                            border: '1px solid rgba(224, 224, 224, 0.3)'
                          }}>
                            <Box sx={{ mb: 0.5 }}>
                              <Typography variant="caption" sx={{ 
                                fontWeight: 'bold', 
                                color: 'text.primary',
                                textTransform: 'uppercase',
                                fontSize: '0.7rem',
                                letterSpacing: '0.5px'
                              }}>
                                Status:
                              </Typography>
                              <Box sx={{ mt: 0.5 }}>
                                {getStatusChip(row)}
                              </Box>
                            </Box>
                            
                            <Box sx={{ 
                              display: 'flex', 
                              flexDirection: 'column',
                              gap: 0.5
                            }}>
                              <Typography variant="caption" sx={{ 
                                fontWeight: 'bold', 
                                color: 'text.primary',
                                textTransform: 'uppercase',
                                fontSize: '0.7rem',
                                letterSpacing: '0.5px'
                              }}>
                                Lab ID:
                              </Typography>
                              <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center',
                                gap: 1,
                                pl: 1
                              }}>
                                <Box sx={{
                                  width: 6,
                                  height: 6,
                                  borderRadius: '50%',
                                  bgcolor: 'primary.main'
                                }} />
                                <Typography variant="body2" sx={{ 
                                  fontWeight: 'bold',
                                  color: 'primary.main'
                                }}>
                                  {row.ApplicationNo || 'N/A'}
                                </Typography>
                              </Box>
                            </Box>
                                  <Box sx={{ 
                              display: 'flex', 
                              flexDirection: 'column',
                              gap: 0.5
                            }}>
                              <Typography variant="caption" sx={{ 
                                fontWeight: 'bold', 
                                color: 'text.primary',
                                textTransform: 'uppercase',
                                fontSize: '0.7rem',
                                letterSpacing: '0.5px'
                              }}>
                                Lab Type:
                              </Typography>
                              <Typography variant="caption" sx={{ 
                                color: 'text.primary',
                                pl: 1.5
                              }}>
                                🏢 {row.LabType || 'N/A'}
                              </Typography>
                            </Box>
                            
                            <Box sx={{ 
                              display: 'flex', 
                              flexDirection: 'column',
                              gap: 0.5
                            }}>
                              <Typography variant="caption" sx={{ 
                                fontWeight: 'bold', 
                                color: 'text.primary',
                                textTransform: 'uppercase',
                                fontSize: '0.7rem',
                                letterSpacing: '0.5px'
                              }}>
                                Laboratory Name:
                              </Typography>
                              <Typography variant="body2" sx={{ 
                                color: 'text.primary',
                                fontStyle: 'italic',
                                pl: 1.5
                              }}>
                                {row.LaboratoryName || 'N/A'}
                              </Typography>
                            </Box>
                            
                            <Box sx={{ 
                              display: 'flex', 
                              flexDirection: 'column',
                              gap: 0.5
                            }}>
                              <Typography variant="caption" sx={{ 
                                fontWeight: 'bold', 
                                color: 'text.primary',
                                textTransform: 'uppercase',
                                fontSize: '0.7rem',
                                letterSpacing: '0.5px'
                              }}>
                                Assessment Type:
                              </Typography>
                              <Typography variant="caption" sx={{ 
                                color: 'success.main',
                                fontWeight: 500,
                                pl: 1.5
                              }}>
                                📋 {row.assessmntype || 'N/A'}
                              </Typography>
                            </Box>
                            
                            <Box sx={{ 
                              display: 'flex', 
                              flexDirection: 'column',
                              gap: 0.5
                            }}>
                              <Typography variant="caption" sx={{ 
                                fontWeight: 'bold', 
                                color: 'text.primary',
                                textTransform: 'uppercase',
                                fontSize: '0.7rem',
                                letterSpacing: '0.5px'
                              }}>
                                User Name:
                              </Typography>
                              <Typography variant="caption" sx={{ pl: 1.5 }}>
                                👤 {row.loginname || 'N/A'}
                              </Typography>
                            </Box>
                            
                          
                          </Box>
                        </TableCell>
                        <TableCell sx={{ 
                          borderBottom: '1px solid rgba(224, 224, 224, 0.5)',
                          py: 2
                        }}>
                          <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            gap: 1,
                            p: 1,
                            bgcolor: 'rgba(0,0,0,0.02)',
                            borderRadius: 2,
                            border: '1px solid rgba(224, 224, 224, 0.3)',
                            minWidth: '220px' 
                          }}>
                            <Typography variant="body2" sx={{ 
                              fontWeight: 'bold',
                              color: 'primary.main',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5
                            }}>
                              👤 {row.ContactPerson || 'N/A'}
                            </Typography>
                            <Typography variant="caption" sx={{ 
                              color: 'info.main', 
                              wordBreak: 'break-all',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5
                            }}>
                              📧 {row.ContactEmail || 'N/A'}
                            </Typography>
                            <Typography variant="caption" sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5
                            }}>
                              📱 {row.ContactMobile || 'N/A'}
                            </Typography>
                            <Typography variant="caption" sx={{ 
                              color: 'text.primary',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5
                            }}>
                              📍 {row.Address || 'N/A'}
                            </Typography>
                            <Typography variant="caption" sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              fontWeight: 500
                            }}>
                              🏛️ {row.State || 'N/A'}, {row.City || 'N/A'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ 
                          borderBottom: '1px solid rgba(224, 224, 224, 0.5)',
                          py: 2
                        }}>
                          <Box sx={{ 
                            maxWidth: '180px',
                            wordWrap: 'break-word',
                            whiteSpace: 'normal',
                            lineHeight: 1.4,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1
                          }}>
                            {(() => {
                              const disciplinesList = formatCommaSeparatedToNumberedList(row.Disciplines);
                              
                              if (!disciplinesList) {
                                return (
                                  <Typography variant="body2" sx={{ 
                                    fontWeight: 500,
                                    wordBreak: 'break-word',
                                    whiteSpace: 'normal'
                                  }}>
                                    Not Specified
                                  </Typography>
                                );
                              }
                              
                              if (typeof disciplinesList === 'string') {
                                // Single item, display as regular text
                                return (
                                  <Typography variant="body2" sx={{ 
                                    fontWeight: 500,
                                    wordBreak: 'break-word',
                                    whiteSpace: 'normal'
                                  }}>
                                    {disciplinesList}
                                  </Typography>
                                );
                              }
                              
                              // Multiple items, display as numbered list
                              return (
                                <Box sx={{ 
                                  display: 'flex', 
                                  flexDirection: 'column', 
                                  gap: 0.5 
                                }}>
                                  {disciplinesList.map((discipline, index) => (
                                    <Typography 
                                      key={index}
                                      variant="body2" 
                                      sx={{ 
                                        fontWeight: 500,
                                        wordBreak: 'break-word',
                                        whiteSpace: 'normal',
                                        fontSize: '0.875rem',
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: 0.5
                                      }}
                                    >
                                      <span style={{ 
                                        fontWeight: 'bold', 
                                        color: '#1976d2',
                                        minWidth: '20px'
                                      }}>
                                        {index + 1}.
                                      </span>
                                      <span>{discipline}</span>
                                    </Typography>
                                  ))}
                                </Box>
                              );
                            })()}
                            {row.Groups && (() => {
                              const groupsList = formatCommaSeparatedToNumberedList(row.Groups);
                              
                              if (typeof groupsList === 'string') {
                                // Single item, display as chip
                                return (
                                  <Chip
                                    label={groupsList}
                                    size="small"
                                    variant="filled"
                                    color="primary"
                                    sx={{ 
                                      alignSelf: 'flex-start',
                                      fontSize: '0.75rem',
                                      height: '20px'
                                    }}
                                  />
                                );
                              }
                              
                              if (Array.isArray(groupsList)) {
                                // Multiple items, display as numbered list with smaller text
                                return (
                                  <Box sx={{ 
                                    display: 'flex', 
                                    flexDirection: 'column', 
                                    gap: 0.3,
                                    mt: 0.5 
                                  }}>
                                    {groupsList.map((group, index) => (
                                      <Typography 
                                        key={index}
                                        variant="caption" 
                                        sx={{ 
                                          fontSize: '0.75rem',
                                          fontWeight: 500,
                                          color: 'primary.main',
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 0.5,
                                          backgroundColor: 'primary.light',
                                          px: 0.8,
                                          py: 0.2,
                                          borderRadius: '4px',
                                          alignSelf: 'flex-start'
                                        }}
                                      >
                                        <span style={{ 
                                          fontWeight: 'bold',
                                          fontSize: '0.7rem'
                                        }}>
                                          {index + 1}.
                                        </span>
                                        <span>{group}</span>
                                      </Typography>
                                    ))}
                                  </Box>
                                );
                              }
                              
                              return null;
                            })()}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ 
                          borderBottom: '1px solid rgba(224, 224, 224, 0.5)',
                          py: 2
                        }}>
                          <Box sx={{ 
                            maxWidth: '200px',
                            wordWrap: 'break-word',
                            whiteSpace: 'normal',
                            lineHeight: 1.4,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1
                          }}>
                            <Typography variant="body2" sx={{ 
                              fontWeight: 500,
                              wordBreak: 'break-word',
                              whiteSpace: 'normal'
                            }}>
                              📅 {row.AssessmentDate || 'Not Scheduled'}
                            </Typography>
                            {row.AssessorName && (() => {
                              const assessorsList = formatCommaSeparatedToNumberedList(row.AssessorName);
                              
                              if (typeof assessorsList === 'string') {
                                // Single assessor
                                return (
                                  <Typography variant="body2" sx={{ 
                                    fontWeight: 400,
                                    wordBreak: 'break-word',
                                    whiteSpace: 'normal',
                                    color: 'text.primary',
                                    fontSize: '0.875rem'
                                  }}>
                                    👤 {assessorsList}
                                  </Typography>
                                );
                              }
                              
                              if (Array.isArray(assessorsList)) {
                                // Multiple assessors, display as numbered list
                                return (
                                  <Box sx={{ 
                                    display: 'flex', 
                                    flexDirection: 'column', 
                                    gap: 0.5,
                                    mt: 0.5
                                  }}>
                                    {assessorsList.map((assessor, index) => (
                                      <Typography 
                                        key={index}
                                        variant="body2" 
                                        sx={{ 
                                          fontWeight: 400,
                                          wordBreak: 'break-word',
                                          whiteSpace: 'normal',
                                          color: 'text.primary',
                                          fontSize: '0.875rem',
                                          display: 'flex',
                                          alignItems: 'flex-start',
                                          gap: 0.5
                                        }}
                                      >
                                        <span>👤</span>
                                        <span style={{ 
                                          fontWeight: 'bold', 
                                          color: '#1976d2',
                                          minWidth: '20px'
                                        }}>
                                          {index + 1}.
                                        </span>
                                        <span>{assessor}</span>
                                      </Typography>
                                    ))}
                                  </Box>
                                );
                              }
                              
                              return null;
                            })()}
                            {!row.AssessorName && (
                              <Typography variant="body2" sx={{ 
                                fontWeight: 400,
                                color: 'text.disabled',
                                fontStyle: 'italic',
                                fontSize: '0.875rem'
                              }}>
                                👤 Not Assigned
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ 
                          borderBottom: '1px solid rgba(224, 224, 224, 0.5)',
                          py: 2
                        }}>
                          <Typography variant="body2" sx={{ 
                            fontWeight: 500,
                            color: row.ProcessedDate ? 'success.main' : 'text.primary'
                          }}>
                            {formatApiDate(row.ProcessedDate) || 'Not Processed'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ 
                          borderBottom: '1px solid rgba(224, 224, 224, 0.5)',
                          py: 2
                        }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {row.AccountName || 'Not Assigned'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ 
                bgcolor: 'grey.50', 
                borderTop: '1px solid rgba(224, 224, 224, 0.5)',
                p: 1
              }}>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  component="div"
                  count={totalRecords}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  sx={{
                    '& .MuiTablePagination-toolbar': {
                      minHeight: '52px'
                    },
                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                      fontWeight: 500
                    }
                  }}
                />
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default ApprovalRequest;